import type {
  AppendMessageRequest,
  ConversationDetailDTO,
  ConversationSummaryDTO,
  CreateConversationRequest,
  ListConversationsResponse,
  MessageDTO,
  UpdateConversationRequest,
} from '@jas/shared';

export type ListConversationsPage = ListConversationsResponse;

/**
 * Conversation history HTTP helpers. Every call requires a Privy access
 * token; anonymous callers shouldn't reach these endpoints (the FE
 * gates on `authenticated` before invoking).
 *
 * Errors are surfaced as thrown `Error`s with a short status-coded
 * message so the caller can decide between retry / surface / ignore
 * without re-parsing the response.
 */
const BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://localhost:3001';

function authHeaders(token: string): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * List the user's conversations. Cursor-paginated; pass `query` as the
 * URL-encoded querystring (e.g. `cursor=abc&limit=25`) for subsequent
 * pages, or undefined for the first page. Returns the full page object
 * including `nextCursor` so the caller can decide whether to fetch more.
 */
export async function listConversations(
  token: string,
  query?: string,
): Promise<ListConversationsPage> {
  const url = `${BASE}/api/conversations${query ? `?${query}` : ''}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`conversations/list failed: ${res.status}`);
  return res.json() as Promise<ListConversationsResponse>;
}

/**
 * Convenience wrapper for callers that don't care about pagination and
 * just want the first page's conversations array. Used by anything that
 * isn't the history drawer.
 */
export async function listFirstConversationsPage(token: string): Promise<ConversationSummaryDTO[]> {
  const page = await listConversations(token);
  return page.conversations;
}

export async function getConversation(
  token: string,
  id: string,
): Promise<ConversationDetailDTO> {
  const res = await fetch(`${BASE}/api/conversations/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`conversations/get failed: ${res.status}`);
  return res.json() as Promise<ConversationDetailDTO>;
}

export async function createConversation(
  token: string,
  body: CreateConversationRequest,
): Promise<ConversationSummaryDTO> {
  const res = await fetch(`${BASE}/api/conversations`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`conversations/create failed: ${res.status}`);
  return res.json() as Promise<ConversationSummaryDTO>;
}

export async function appendMessage(
  token: string,
  conversationId: string,
  body: AppendMessageRequest,
): Promise<MessageDTO> {
  const res = await fetch(`${BASE}/api/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`conversations/append failed: ${res.status}`);
  return res.json() as Promise<MessageDTO>;
}

export async function updateConversation(
  token: string,
  conversationId: string,
  body: UpdateConversationRequest,
): Promise<ConversationSummaryDTO> {
  const res = await fetch(`${BASE}/api/conversations/${conversationId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`conversations/update failed: ${res.status}`);
  return res.json() as Promise<ConversationSummaryDTO>;
}

export async function deleteConversation(token: string, conversationId: string): Promise<void> {
  const res = await fetch(`${BASE}/api/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`conversations/delete failed: ${res.status}`);
}

/**
 * Bulk-delete every conversation the signed-in user owns. Returns the
 * count of rows that were actually deleted (0 if history was already
 * empty — not an error). The caller is responsible for confirming
 * with the user before invoking; this helper just trusts the call.
 */
export async function deleteAllConversations(token: string): Promise<{ deletedCount: number }> {
  const res = await fetch(`${BASE}/api/conversations`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`conversations/deleteAll failed: ${res.status}`);
  return res.json() as Promise<{ deletedCount: number }>;
}
